/**
 * ToolChipTooltip - Portal-free fixed-position tooltip for tool chips
 *
 * Uses `position: fixed` to escape overflow containers (same pattern as
 * VirtualServerTabs). Measures the trigger element via getBoundingClientRect
 * and flips vertically when the tooltip would clip the viewport edge.
 *
 * Render ONE instance per parent and drive it via show/hide + content props.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';

export interface TooltipContent {
  name: string;
  description: string;
}

interface Position {
  x: number;
  y: number;
  placement: 'above' | 'below';
}

const SHOW_DELAY_MS = 200;
const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 12;
const MIN_SPACE_ABOVE = 80;

export function useToolChipTooltip() {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState<TooltipContent | null>(null);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0, placement: 'above' });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipIdRef = useRef(`uts-tooltip-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const show = useCallback((
    toolName: string,
    toolDescription: string,
    triggerElement: HTMLElement,
  ) => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const rect = triggerElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    // Prefer above; fall back to below if too close to top
    const spaceAbove = rect.top;
    const placement = spaceAbove < MIN_SPACE_ABOVE ? 'below' : 'above';
    const y = placement === 'above'
      ? rect.top - TOOLTIP_GAP
      : rect.bottom + TOOLTIP_GAP;

    setContent({ name: toolName, description: toolDescription });
    setPosition({ x: centerX, y, placement });

    timerRef.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
  }, []);

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  return { visible, content, position, show, hide, tooltipId: tooltipIdRef.current };
}

interface ToolChipTooltipProps {
  visible: boolean;
  content: TooltipContent | null;
  position: Position;
  tooltipId: string;
}

export const ToolChipTooltip: React.FC<ToolChipTooltipProps> = ({
  visible,
  content,
  position,
  tooltipId,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [adjustedX, setAdjustedX] = useState<number | null>(null);

  // Adjust horizontal position after render to prevent viewport overflow
  useEffect(() => {
    if (!visible || !tooltipRef.current) {
      setAdjustedX(null);
      return;
    }

    const el = tooltipRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;

    let newX = position.x;

    // Clamp left edge
    const halfWidth = rect.width / 2;
    if (newX - halfWidth < VIEWPORT_PADDING) {
      newX = halfWidth + VIEWPORT_PADDING;
    }
    // Clamp right edge
    if (newX + halfWidth > vw - VIEWPORT_PADDING) {
      newX = vw - halfWidth - VIEWPORT_PADDING;
    }

    if (newX !== position.x) {
      setAdjustedX(newX);
    } else {
      setAdjustedX(null);
    }
  }, [visible, position]);

  if (!visible || !content) return null;

  const x = adjustedX ?? position.x;
  const isAbove = position.placement === 'above';

  return (
    <>
      <div
        ref={tooltipRef}
        id={tooltipId}
        data-testid="uts-tool-chip-tooltip"
        role="tooltip"
        style={{
          position: 'fixed',
          left: x,
          top: position.y,
          transform: isAbove
            ? 'translate(-50%, -100%)'
            : 'translateX(-50%)',
          padding: '10px 14px',
          background: 'var(--tooltip-bg, rgba(15, 23, 42, 0.96))',
          border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
          borderRadius: 10,
          boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.35))',
          zIndex: 10000,
          maxWidth: 340,
          minWidth: 160,
          pointerEvents: 'none',
          animation: 'utsTooltipIn 0.15s ease',
        }}
      >
        {/* Tool name (bold, monospace) */}
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--main-accent, #6C4CFF)',
          fontFamily: 'var(--font-mono, monospace)',
          marginBottom: content.description ? 6 : 0,
          wordBreak: 'break-all',
          lineHeight: 1.3,
        }}>
          {content.name}
        </div>

        {/* Description */}
        {content.description && (
          <div style={{
            fontSize: 11,
            fontWeight: 400,
            color: 'var(--main-text, rgba(255,255,255,0.88))',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}>
            {content.description}
          </div>
        )}

        {/* Arrow caret */}
        <div style={{
          position: 'absolute',
          ...(isAbove
            ? { bottom: -6, borderTop: '6px solid var(--border-color, rgba(255,255,255,0.1))' }
            : { top: -6, borderBottom: '6px solid var(--border-color, rgba(255,255,255,0.1))' }),
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
        }} />
      </div>

      <style>{`
        @keyframes utsTooltipIn {
          from { opacity: 0; transform: translate(-50%, ${isAbove ? 'calc(-100% + 4px)' : '-4px'}); }
          to   { opacity: 1; transform: translate(-50%, ${isAbove ? '-100%' : '0'}); }
        }
      `}</style>
    </>
  );
};
